import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaArrowLeft, FaCheckCircle, FaArrowRight, FaUser, FaEnvelope, 
  FaPhone, FaBuilding, FaGlobe, FaBriefcase, FaRocket, FaStar,
  FaShieldAlt, FaClock, FaHeadset
} from 'react-icons/fa';
import { IoMdFlash } from 'react-icons/io';

const GetStartedPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobileNumber: '',
    companyName: '',
    country: '',
    role: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  // Countries list
  const countries = [
    'Select Country', 'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 
    'France', 'India', 'Singapore', 'Japan', 'Brazil', 'Mexico', 
    'Netherlands', 'Spain', 'Italy', 'Sweden', 'Norway', 'Denmark',
    'South Africa', 'UAE', 'Saudi Arabia', 'Other'
  ];

  // Roles list
  const roles = [
    'Select Role', 'Sales Director', 'Marketing Manager', 'CEO/Founder', 'Head of Sales',
    'Revenue Operations', 'Business Development', 'Sales Representative',
    'Marketing Specialist', 'Product Manager', 'Other'
  ];

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = 'Mobile number is required';
    } else if (!/^[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{3,4}[-\s\.]?[0-9]{3,4}$/.test(formData.mobileNumber)) {
      newErrors.mobileNumber = 'Please enter a valid phone number';
    }
    return newErrors;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';
    if (!formData.country || formData.country === 'Select Country') newErrors.country = 'Country is required';
    if (!formData.role || formData.role === 'Select Role') newErrors.role = 'Role is required';
    return newErrors;
  };

  const handleNext = () => {
    const stepErrors = validateStep1();
    if (Object.keys(stepErrors).length === 0) {
      setCurrentStep(2);
      setErrors({});
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setErrors(stepErrors);
    }
  };

  const handleBack = () => {
    setCurrentStep(1);
    setErrors({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const stepErrors = validateStep2();
    
    if (Object.keys(stepErrors).length === 0) {
      setIsSubmitting(true);
      
      // Simulate API call
      setTimeout(() => {
        console.log('Form submitted:', formData);
        setIsSubmitting(false);
        setIsSubmitted(true);
        
        // Reset form after 2 seconds and redirect
        setTimeout(() => {
          navigate('/', { state: { success: true } });
        }, 2000);
      }, 1500);
    } else {
      setErrors(stepErrors);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#0A0A0F] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <FaCheckCircle className="text-green-400 text-5xl" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-3">Welcome to LeadFlow AI! 🎉</h2>
          <p className="text-gray-400 text-lg mb-6">
            Thank you for signing up! Our team will contact you within 24 hours to schedule your onboarding.
          </p>
          <div className="space-y-3 mb-6">
            <div className="flex items-center justify-center gap-2 text-sm text-gray-300">
              <FaClock className="text-purple-400" />
              <span>Setup takes just 15 minutes</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-gray-300">
              <FaHeadset className="text-purple-400" />
              <span>Dedicated support included</span>
            </div>
          </div>
          <button
            onClick={() => navigate('/')}
            className="bg-gradient-to-r from-purple-600 to-indigo-600 px-8 py-3 rounded-full font-semibold hover:shadow-lg transition-all duration-300"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0F]">
      {/* Navigation */}
      <div className="fixed top-0 left-0 w-full z-50 bg-[rgba(10,10,15,0.9)] backdrop-blur-xl border-b border-purple-500/30 shadow-lg">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <IoMdFlash className="text-purple-400 text-3xl" />
            <span className="text-2xl font-bold bg-gradient-to-r from-indigo-300 via-purple-400 to-indigo-300 bg-clip-text text-transparent">
              LeadFlow AI
            </span>
          </div>
          <button
            onClick={() => navigate('/')}
            className="text-gray-300 hover:text-white transition-all duration-300 flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-white/5"
          >
            <FaArrowLeft /> Back to Home
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="pt-32 pb-20 max-w-4xl mx-auto px-5 md:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500/20 to-indigo-500/20 rounded-full px-4 py-1.5 border border-purple-500/30 mb-4">
            <FaRocket className="text-purple-400" />
            <span className="text-sm font-medium">Start Your Free Trial</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Get Started with <span className="bg-gradient-to-r from-purple-400 to-blue-500 bg-clip-text text-transparent">LeadFlow AI</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Join 2,000+ companies using AI to scale their outbound. Start your 14-day free trial today.
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-[rgba(18,22,35,0.6)] backdrop-blur-xl border border-purple-500/30 rounded-2xl p-6 md:p-8 shadow-2xl">
          {/* Step Indicator */}
          <div className="mb-8">
            <div className="flex gap-3 mb-2">
              <div className={`flex-1 h-2 rounded-full transition-all duration-500 ${
                currentStep >= 1 ? 'bg-gradient-to-r from-purple-500 to-indigo-500' : 'bg-white/10'
              }`}></div>
              <div className={`flex-1 h-2 rounded-full transition-all duration-500 ${
                currentStep >= 2 ? 'bg-gradient-to-r from-purple-500 to-indigo-500' : 'bg-white/10'
              }`}></div>
            </div>
            <div className="flex justify-between text-sm">
              <span className={currentStep >= 1 ? 'text-purple-400' : 'text-gray-500'}>Personal Information</span>
              <span className={currentStep >= 2 ? 'text-purple-400' : 'text-gray-500'}>Company Details</span>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {currentStep === 1 ? (
              // Step 1: Personal Information
              <div className="space-y-5">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      First Name <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <FaUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm" />
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        className={`w-full pl-10 pr-3 py-3 bg-white/5 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 text-white ${
                          errors.firstName ? 'border-red-500 focus:ring-red-500/20' : 'border-white/10 focus:border-purple-500 focus:ring-purple-500/20'
                        }`}
                        placeholder="John"
                      />
                    </div>
                    {errors.firstName && <p className="text-red-400 text-xs mt-1">{errors.firstName}</p>}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Last Name <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <FaUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm" />
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        className={`w-full pl-10 pr-3 py-3 bg-white/5 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 text-white ${
                          errors.lastName ? 'border-red-500 focus:ring-red-500/20' : 'border-white/10 focus:border-purple-500 focus:ring-purple-500/20'
                        }`}
                        placeholder="Doe"
                      />
                    </div>
                    {errors.lastName && <p className="text-red-400 text-xs mt-1">{errors.lastName}</p>}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-3 py-3 bg-white/5 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 text-white ${
                        errors.email ? 'border-red-500 focus:ring-red-500/20' : 'border-white/10 focus:border-purple-500 focus:ring-purple-500/20'
                      }`}
                      placeholder="john@company.com"
                    />
                  </div>
                  {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Mobile Number <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <FaPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm" />
                    <input
                      type="tel"
                      name="mobileNumber"
                      value={formData.mobileNumber}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-3 py-3 bg-white/5 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 text-white ${
                        errors.mobileNumber ? 'border-red-500 focus:ring-red-500/20' : 'border-white/10 focus:border-purple-500 focus:ring-purple-500/20'
                      }`}
                      placeholder="+1 234 567 8900"
                    />
                  </div>
                  {errors.mobileNumber && <p className="text-red-400 text-xs mt-1">{errors.mobileNumber}</p>}
                </div>
                
                <div className="flex justify-end pt-4">
                  <button
                    type="button"
                    onClick={handleNext}
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 px-8 py-3 rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/30 transition-all duration-300 flex items-center gap-2 text-white"
                  >
                    Next Step
                    <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            ) : (
              // Step 2: Company Information
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Company Name <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <FaBuilding className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm" />
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-3 py-3 bg-white/5 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 text-white ${
                        errors.companyName ? 'border-red-500 focus:ring-red-500/20' : 'border-white/10 focus:border-purple-500 focus:ring-purple-500/20'
                      }`}
                      placeholder="Tech Corp Inc."
                    />
                  </div>
                  {errors.companyName && <p className="text-red-400 text-xs mt-1">{errors.companyName}</p>}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Country <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <FaGlobe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm" />
                    <select
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-10 py-3 bg-white/5 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 appearance-none cursor-pointer text-white ${
                        errors.country ? 'border-red-500 focus:ring-red-500/20' : 'border-white/10 focus:border-purple-500 focus:ring-purple-500/20'
                      }`}
                    >
                      {countries.map((country, idx) => (
                        <option key={idx} value={country} className="bg-[#0F111A] text-white">{country}</option>
                      ))}
                    </select>
                  </div>
                  {errors.country && <p className="text-red-400 text-xs mt-1">{errors.country}</p>}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Role <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <FaBriefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm" />
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-10 py-3 bg-white/5 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 appearance-none cursor-pointer text-white ${
                        errors.role ? 'border-red-500 focus:ring-red-500/20' : 'border-white/10 focus:border-purple-500 focus:ring-purple-500/20'
                      }`}
                    >
                      {roles.map((role, idx) => (
                        <option key={idx} value={role} className="bg-[#0F111A] text-white">{role}</option>
                      ))}
                    </select>
                  </div>
                  {errors.role && <p className="text-red-400 text-xs mt-1">{errors.role}</p>}
                </div>
                
                {/* Benefits Box */}
                <div className="bg-gradient-to-r from-purple-500/10 to-indigo-500/10 rounded-xl p-5 border border-purple-500/20">
                  <div className="flex items-center gap-2 mb-3">
                    <FaStar className="text-yellow-400" />
                    <span className="font-semibold text-white">What you'll get with your free trial:</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm text-gray-300">
                    <div className="flex items-center gap-2">
                      <FaCheckCircle className="text-green-400 text-xs" />
                      <span>14-day free trial</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FaCheckCircle className="text-green-400 text-xs" />
                      <span>AI-powered prospecting</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FaCheckCircle className="text-green-400 text-xs" />
                      <span>Priority email support</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FaCheckCircle className="text-green-400 text-xs" />
                      <span>30-min onboarding call</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FaCheckCircle className="text-green-400 text-xs" />
                      <span>Unlimited team members</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FaCheckCircle className="text-green-400 text-xs" />
                      <span>API access</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={handleBack}
                    className="flex-1 px-6 py-3 rounded-xl border border-gray-600 hover:bg-white/5 transition-all duration-300 text-white"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-3 rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/30 transition-all duration-300 flex items-center justify-center gap-2 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        Start Free Trial
                        <FaRocket className="group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </form>
          
          {/* Trust Badges */}
          <div className="mt-8 pt-6 border-t border-white/10">
            <div className="flex flex-wrap justify-center gap-6 text-xs text-gray-500">
              <div className="flex items-center gap-2">
                <FaShieldAlt className="text-green-400" />
                <span>Secure & Encrypted</span>
              </div>
              <div className="flex items-center gap-2">
                <FaClock className="text-purple-400" />
                <span>No credit card required</span>
              </div>
              <div className="flex items-center gap-2">
                <FaHeadset className="text-blue-400" />
                <span>24/7 support</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GetStartedPage;